import React from 'react';
import {
  MONTH_NAMES,
  WEEKDAY_HEADER,
  STATE_COLOR,
  SCOPE_COLOR,
  fmtLocalDate,
  parseLocalDate
} from '../../utils/scheduleCalendar';

function MiniMonthGrid({
  year,
  month,
  rangeStart,
  rangeEnd,
  dayStates,
  dayScopes,
  trackOverlayDays,
  referenceDateIso
}) {
  const firstOfMonth = new Date(year, month, 1);
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const leadingBlanks = firstOfMonth.getDay();
  const refDate = referenceDateIso ? parseLocalDate(referenceDateIso) : null;

  const cells = [];
  for (let i = 0; i < leadingBlanks; i++) cells.push({ blank: true, key: `b${i}` });
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    const inRange = date >= rangeStart && date <= rangeEnd;
    const key = fmtLocalDate(date);
    const state = inRange ? dayStates.get(key) || 'off' : null;
    const overlay = trackOverlayDays?.has(key);
    const scope = inRange && dayScopes ? dayScopes.get(key) : null;
    const isToday =
      refDate &&
      date.getFullYear() === refDate.getFullYear() &&
      date.getMonth() === refDate.getMonth() &&
      date.getDate() === refDate.getDate();
    cells.push({ blank: false, key, day: d, state, overlay, inRange, scope, isToday });
  }
  while (cells.length % 7 !== 0) cells.push({ blank: true, key: `t${cells.length}` });

  return (
    <div className="ws-lp-cal-month">
      <div className="ws-lp-cal-month-title">
        {MONTH_NAMES[month]} {year}
      </div>
      <div className="ws-lp-cal-weekdays">
        {WEEKDAY_HEADER.map((w, i) => (
          <div key={i} className="ws-lp-cal-weekday">
            {w}
          </div>
        ))}
      </div>
      <div className="ws-lp-cal-days">
        {cells.map((c) => {
          if (c.blank) {
            return <div key={c.key} className="ws-lp-cal-day ws-lp-cal-day--blank" aria-hidden />;
          }
          if (!c.inRange) {
            return (
              <div key={c.key} className="ws-lp-cal-day ws-lp-cal-day--out">
                {c.day}
              </div>
            );
          }
          const palette = STATE_COLOR[c.state] || STATE_COLOR.off;
          const scopeLetter =
            c.scope === 'students' ? 'S' : c.scope === 'staff' ? 'T' : null;
          const scopeTitle =
            c.scope === 'students'
              ? ' · students only'
              : c.scope === 'staff'
                ? ' · staff only'
                : '';
          return (
            <div
              key={c.key}
              className={[
                'ws-lp-cal-day',
                c.overlay ? 'ws-lp-cal-day--track' : '',
                c.isToday ? 'ws-lp-cal-day--today' : ''
              ]
                .filter(Boolean)
                .join(' ')}
              style={{
                background: palette.bg,
                color: palette.text,
                borderColor: palette.border
              }}
              title={`${c.key} — ${c.state}${c.overlay ? ' · track day' : ''}${scopeTitle}`}
            >
              {c.day}
              {scopeLetter && (
                <span
                  className="ws-lp-cal-scope"
                  style={{ color: SCOPE_COLOR[c.scope] }}
                  aria-hidden
                >
                  {scopeLetter}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function LpMiniCalendar({ section, referenceDateIso, loading, error }) {
  if (loading) {
    return <p className="ws-lp-cal-loading">Loading calendar…</p>;
  }
  if (error) {
    return <p className="ws-lp-cal-error">{error}</p>;
  }
  if (!section) {
    return null;
  }

  const { counts } = section;

  return (
    <div className="ws-lp-cal">
      <div className="ws-lp-cal-stats">
        <span className="ws-lp-cal-stat ws-lp-cal-stat--session">
          <strong>{counts.session}</strong> session
        </span>
        {counts.added > 0 && (
          <span className="ws-lp-cal-stat ws-lp-cal-stat--added">
            <strong>{counts.added}</strong> added
          </span>
        )}
        {counts.removed > 0 && (
          <span className="ws-lp-cal-stat ws-lp-cal-stat--removed">
            <strong>{counts.removed}</strong> removed
          </span>
        )}
      </div>
      <div className="ws-lp-cal-months">
        {section.months.map(({ year, month }) => (
          <MiniMonthGrid
            key={`${year}-${month}`}
            year={year}
            month={month}
            rangeStart={section.rangeStart}
            rangeEnd={section.rangeEnd}
            dayStates={section.dayStates}
            dayScopes={section.dayScopes}
            trackOverlayDays={section.overlayDays}
            referenceDateIso={referenceDateIso}
          />
        ))}
      </div>
      <div className="ws-lp-cal-legend" aria-label="Calendar legend">
        <span className="ws-lp-cal-legend-item">
          <span className="ws-lp-cal-swatch ws-lp-cal-swatch--session" /> Session
        </span>
        <span className="ws-lp-cal-legend-item">
          <span className="ws-lp-cal-swatch ws-lp-cal-swatch--added" /> Added
        </span>
        <span className="ws-lp-cal-legend-item">
          <span className="ws-lp-cal-swatch ws-lp-cal-swatch--removed" /> Removed
        </span>
        <span className="ws-lp-cal-legend-item">
          <span className="ws-lp-cal-swatch ws-lp-cal-swatch--track" /> Track day
        </span>
        <span className="ws-lp-cal-legend-item">
          <span className="ws-lp-cal-scope-legend ws-lp-cal-scope-legend--students">S</span>
          Students only
        </span>
        <span className="ws-lp-cal-legend-item">
          <span className="ws-lp-cal-scope-legend ws-lp-cal-scope-legend--staff">T</span>
          Staff only
        </span>
      </div>
    </div>
  );
}
